import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonColor, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useEscalationSound } from "../../../Hooks/useEscalationSound";
import BotPresenceManager from "../../../Models/ContactCenterV3/BotPresence.Manager";
import ContactCenterV3Model from "../../../Models/ContactCenterV3/ContactCenterV3.Store";
import { isNotificationLeader } from "../../../Models/ContactCenterV3/NotificationLeader";
import MembersModel from "../../../Models/Member/Members.Model";

// ─── Constants ─────────────────────────────────────────────────────────────

/** Fallback when the offer carries no server deadline (older payloads). */
const FALLBACK_DISMISS_MS = 30_000;

/** Orange ring draining clockwise with the offer's remaining time (Figma toast spec). */
function CountdownRing({ secondsLeft, totalSeconds }: { secondsLeft: number; totalSeconds: number }): React.ReactElement {
  const radius = 13;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.max(0, Math.min(1, secondsLeft / Math.max(totalSeconds, 1)));
  return (
    <span className="relative inline-flex h-8 w-8 items-center justify-center">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 32 32" aria-hidden>
        <circle cx="16" cy="16" r={radius} fill="none" strokeWidth="3" className="stroke-grey-100" />
        <circle
          cx="16"
          cy="16"
          r={radius}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          className="stroke-orange-500 transition-[stroke-dashoffset] duration-1000 ease-linear"
        />
      </svg>
      <span className="text-[10px] font-semibold text-orange-600">{secondsLeft}</span>
    </span>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────

export function AssignmentToast(): React.ReactElement | null {
  const { pendingAssignmentToast, missedOfferNotices, presenceNotice, ccNotices } = ContactCenterV3Model.useState();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Play notification sound while toast is showing — leader tab only (R9,
  // ENG-2827): with the CC open in multiple tabs, exactly one plays.
  useEscalationSound(!!pendingAssignmentToast && isNotificationLeader());

  // Animate in when toast appears + browser push notification if tab not focused
  useEffect(() => {
    if (pendingAssignmentToast) {
      const raf = requestAnimationFrame(() => setIsVisible(true));

      // The browser notification for an offer is the server web push
      // (ESCALATION_AGENT_ASSIGNED): it reaches a closed Studio too, honours the
      // member's notification preferences, and its click lands on the conversation.
      // Until ENG-3475 this toast raised a second, non-navigating one. Only the
      // permission ask stays here, so the push can be shown at all.
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }

      return () => cancelAnimationFrame(raf);
    }
    setIsVisible(false);
    return undefined;
  }, [pendingAssignmentToast]);

  // Countdown from the SERVER deadline (ENG-2826) — never a client-local
  // timer, so an offer re-delivered after a reconnect shows the true
  // remaining time. Dismisses itself at expiry (the server reroutes then).
  useEffect(() => {
    if (!pendingAssignmentToast) {
      setSecondsLeft(null);
      return undefined;
    }

    const expiresAtMs = pendingAssignmentToast.expiresAt ? new Date(pendingAssignmentToast.expiresAt).getTime() : Date.now() + FALLBACK_DISMISS_MS;

    const tick = (): void => {
      const remaining = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        // Watched to zero on screen — not "missed"; don't re-raise it as a
        // missed notice on the next resync.
        ContactCenterV3Model.markOfferExpiryWitnessed(pendingAssignmentToast.assignmentId);
        ContactCenterV3Model.dismissAssignmentToast();
      }
    };
    tick();
    const timer = setInterval(tick, 1_000);
    return () => clearInterval(timer);
  }, [pendingAssignmentToast]);

  const toast = pendingAssignmentToast;

  // First remaining-seconds seen for this offer = the ring's full scale, so a
  // re-delivered offer drains from wherever its server deadline actually is.
  const totalSecondsRef = useRef<number | null>(null);
  useEffect(() => {
    totalSecondsRef.current = null;
  }, [pendingAssignmentToast?.assignmentId]);
  if (secondsLeft != null && totalSecondsRef.current == null) {
    totalSecondsRef.current = Math.max(secondsLeft, 1);
  }

  const handleAccept = async (): Promise<void> => {
    if (!toast) return;
    await ContactCenterV3Model.acceptAssignment(toast.assignmentId, { channel: toast.channel, botConversationId: toast.botConversationId });
    ContactCenterV3Model.dismissAssignmentToast();
    // Accepting means "I'm handling this now" — jump straight to the conversation.
    // The deep-link param lands on the escalated channel's tab (ENG-3408): a bare
    // user URL auto-selects the user's first channel, which is often not it.
    if (toast.userId) {
      const deepLink = toast.botConversationId ? `?conversation=${toast.botConversationId}` : "";
      navigate(`/contact-center-v3/conversations/${toast.userId}${deepLink}`);
    }
  };

  // Ignore = reject (decided §8.3): the server reroutes to the next agent
  // immediately instead of the offer silently running out its clock.
  const handleIgnore = (): void => {
    if (!toast) return;
    void ContactCenterV3Model.rejectAssignment(toast.assignmentId);
    ContactCenterV3Model.dismissAssignmentToast();
  };

  // Every notice kind this stack can render must be in this guard, or it
  // bails before drawing one that has no offer alongside it.
  if (!toast && missedOfferNotices.length === 0 && !presenceNotice && ccNotices.length === 0) return null;

  const handleGoBackOnline = async (): Promise<void> => {
    ContactCenterV3Model.dismissPresenceNotice();
    // Availability is the source of truth for whether routing can reach you
    // (ENG-3098) — so turning it back on is what "back online" means.
    await MembersModel.setAvailability(true);
    // Don't wait up to 30s for the next tick to re-establish presence.
    BotPresenceManager.heartbeat();
  };

  // Unified bottom-right toast stack (plan §5: offers with countdown ring +
  // missed-one notices stack together; Flow 4 edge case).
  return (
    <div className="fixed right-4 bottom-4 z-500 flex w-80 flex-col gap-2">
      {toast && (
        <div
          className={filterClasses(
            "rounded-lg border border-grey-200 bg-white p-4 shadow-lg transition-all duration-300",
            isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          )}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-grey-800">New Escalation</p>
            {secondsLeft != null && <CountdownRing secondsLeft={secondsLeft} totalSeconds={totalSecondsRef.current ?? secondsLeft} />}
          </div>
          <p className="mt-1 text-sm text-grey-600">
            {toast.userName}
            {toast.reason && <span className="text-grey-400"> — {toast.reason}</span>}
          </p>
          <div className="mt-3 flex gap-2">
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={handleIgnore}>
              Ignore
            </Button>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} color={ButtonColor.TEAL} onClick={handleAccept}>
              Accept
            </Button>
          </div>
        </div>
      )}

      {missedOfferNotices.map((m) => (
        <div key={m.assignmentId} className="rounded-lg border border-orange-500 bg-white p-4 shadow-lg">
          <p className="text-sm font-semibold text-grey-800">You missed this one</p>
          <p className="mt-1 text-sm text-grey-600">
            {m.userName} <span className="text-grey-400">— {m.channel}</span>
          </p>
          <div className="mt-3 flex gap-2">
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => ContactCenterV3Model.dismissMissedOffer(m.assignmentId)}>
              Dismiss
            </Button>
          </div>
        </div>
      ))}

      {/* Manager changed this agent's status (ENG-3098). In THIS stack, not
          react-toastify's top-right container — it belongs with the other
          alerts about the agent's own work state, which is where the design
          shows it. */}
      {presenceNotice && (
        <div className="rounded-lg border border-grey-200 bg-white p-4 shadow-lg">
          <p className="text-sm font-semibold text-grey-800">Status changed</p>
          <p className="mt-1 text-sm text-grey-600">{presenceNotice}</p>
          <div className="mt-3 flex gap-2">
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => ContactCenterV3Model.dismissPresenceNotice()}>
              Dismiss
            </Button>
            {/* Only the agent can put themselves back on — which is exactly
                what this does, so it belongs here rather than on the manager's
                side (ENG-3052). It flips their own availability back on and
                heartbeats immediately, so they are routable again at once
                instead of on the next 30s tick. */}
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} color={ButtonColor.TEAL} onClick={handleGoBackOnline}>
              Go Back Online
            </Button>
          </div>
        </div>
      )}

      {/* General CC action confirmations/errors — same stack, never
          react-toastify top-right (Chuck, 2026-08-24). */}
      {ccNotices.map((notice) => (
        <div
          key={notice.id}
          className={filterClasses("rounded-lg border bg-white p-4 shadow-lg", notice.kind === "error" ? "border-red-600" : "border-grey-200")}
        >
          <p className="text-sm text-grey-800">{notice.text}</p>
          <div className="mt-3 flex gap-2">
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => ContactCenterV3Model.dismissCCNotice(notice.id)}>
              Dismiss
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
